import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { apiJson, setAuthTokens } from '../../utils/apiClient';
import { useAndroidBackButton } from '../../hooks/useAndroidBackButton';
import { isAndroidCapacitor } from '../../utils/platformUtils';
import { sessionIsAdmin } from '../../utils/adminAuth';
import './Login.css';
import FooterSimple from '../../components/Footer/FooterSimple';
import PasswordField from '../../components/PasswordField/PasswordField';

const LoginPage = () => {
    const { user, setUser } = useContext(UserContext);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.redirectTo || '/';
    const warningMessage = location.state?.message || null;
    const verifyEmailMesage = location.state?.verifyEmailMesage || null;

    // Already signed in as platform admin — stay on the admin portal.
    useEffect(() => {
        if (sessionIsAdmin(user)) {
            navigate('/admin/overview', { replace: true });
        }
    }, [user, navigate]);

    // Android detection
    const isAndroidWizard = isAndroidCapacitor();

    // Add Android class to body for CSS targeting
    useEffect(() => {
        if (!isAndroidWizard) return;
        document.body.classList.add('is-android-capacitor');
        return () => document.body.classList.remove('is-android-capacitor');
    }, [isAndroidWizard]);

    // Keyboard-aware state for Android
    const [kbHeight, setKbHeight] = useState(0);
    const [adHeight] = useState(0);
    const scrollContainerRef = useRef(null);

    // Center the focused input within scroll container
    const centerActiveInput = useCallback(() => {
        const sc = scrollContainerRef.current;
        if (!sc) return;

        const active = document.activeElement;
        if (!active) return;

        const target = active.closest('input, textarea, [contenteditable="true"]') || active;

        const scRect = sc.getBoundingClientRect();
        const tRect = target.getBoundingClientRect();

        // distance from top of container to target
        const offset = (tRect.top - scRect.top) + sc.scrollTop;
        
        // Adjust centering to position input higher in the available space
        // Use 40% from top instead of 50% for better visual balance
        const desiredTop = offset - (sc.clientHeight * 0.4) + (tRect.height / 2);

        const maxScrollable = Math.max(0, sc.scrollHeight - sc.clientHeight);
        const top = Math.max(0, Math.min(desiredTop, maxScrollable));

        sc.scrollTo({ top, behavior: 'smooth' });

        // last-ditch fallback (helps some OEM WebViews)
        if ('scrollIntoView' in target) {
            setTimeout(() => {
                if (Math.abs(sc.scrollTop - top) < 2) {
                    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            }, 120);
        }
    }, []);

    // Keyboard event listeners for Android
    useEffect(() => {
        if (!isAndroidWizard) return;

        let removeAll = undefined;

        (async () => {
            try {
                const { Keyboard } = await import('@capacitor/keyboard');
                try { await Keyboard.setResizeMode({ mode: 'native' }); } catch {}

                const subs = [
                    Keyboard.addListener('keyboardWillShow', (e) => {
                        setKbHeight(e.keyboardHeight || 0);
                        setTimeout(centerActiveInput, 60);
                    }),
                    Keyboard.addListener('keyboardDidShow', (e) => {
                        setKbHeight(e.keyboardHeight || 0);
                        setTimeout(centerActiveInput, 60);
                    }),
                    Keyboard.addListener('keyboardWillHide', () => {
                        setKbHeight(0);
                    }),
                    Keyboard.addListener('keyboardDidHide', () => {
                        setKbHeight(0);
                    }),
                ];

                removeAll = () => subs.forEach(s => s.remove());
            } catch (err) {
                console.log('Keyboard plugin not available:', err);
            }
        })();

        return () => removeAll?.();
    }, [isAndroidWizard, centerActiveInput]);

    // Recompute scroll container constraints when sizes change
    useEffect(() => {
        if (!isAndroidWizard || !scrollContainerRef.current) return;
        
        // Try to get header height from various sources
        const headerH = 
            (document.querySelector('#app-header, .app-header')?.getBoundingClientRect()?.height || 0);
        
        const available = Math.max(200, window.innerHeight - headerH - adHeight);
        const sc = scrollContainerRef.current;
        sc.style.maxHeight = `${available}px`;
        sc.style.paddingBottom = `${kbHeight + adHeight}px`;
    }, [isAndroidWizard, kbHeight, adHeight]);

    // Focus event listener for centering
    useEffect(() => {
        if (!isAndroidWizard) return;
        const onFocusIn = () => setTimeout(centerActiveInput, 60);
        document.addEventListener('focusin', onFocusIn);
        return () => document.removeEventListener('focusin', onFocusIn);
    }, [isAndroidWizard, centerActiveInput]);

    // Handle Android back button for login page
    useAndroidBackButton();

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'email' && value.includes('@')) {
            setFormData({ ...formData, [name]: value.toLowerCase() });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const data = await apiJson('/Users/login', { body: formData, auth: false });
            if (data.isSuccess) {
                setAuthTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
                const userProfile = {
                    ...data.userProfileInfo,
                    isAdmin: Boolean(data.isAdmin),
                };
                setUser(userProfile);
                localStorage.setItem('user', JSON.stringify(userProfile));
                window.scrollTo(0, 0);
                // Sole admin email uses the same login, then stays on the admin portal only.
                navigate(data.isAdmin ? '/admin/overview' : redirectTo);
            } else {
                setError(data.errorMessageUserValidation);
                if (data.isNoEmailVerificationError) {
                    setError(
                        <>
                            {data.errorMessageUserValidation}{' '}
                            <span
                                onClick={() => navigate('/resendEmailVerification')}
                                style={{ color: '#3f7a65', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                                Click Here
                            </span>{' '}
                            to resend your email verification email.
                        </>
                    );
                }
            }
        } catch (err) {
            console.error('Error during login:', err);
            const offline = err instanceof TypeError && /failed to fetch/i.test(String(err.message || ''));
            setError(offline
                ? 'Cannot reach the API. Make sure the backend is running, then try again.'
                : 'Incorrect email or password.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = () => {
        navigate('/resetpassword');
    };

    const handleCreateAccount = () => {
        navigate('/#HomePage-community-section', { state: { redirectTo } });
    };

    // Render the login form
    const renderForm = () => (
        <div className="msv-auth-card">
            <p className="msv-auth-eyebrow">Pit Lane Access</p>
            <h2>Log In to Your Vault</h2>
            {warningMessage && (
                <div
                    className="login-warning-box"
                    style={{
                        color: '#a12622',
                        backgroundColor: '#fdecea',
                        padding: '10px 14px',
                        marginBottom: '20px',
                        borderRadius: '6px',
                        border: '1px solid rgba(229, 57, 53, 0.3)',
                        borderLeft: '4px solid #e53935',
                    }}
                >
                    {warningMessage}
                </div>
            )}
            {verifyEmailMesage && (
                <div
                    className="login-warning-box"
                    style={{
                        color: '#2e6b52',
                        backgroundColor: '#e7f4ee',
                        padding: '10px 14px',
                        marginBottom: '20px',
                        borderRadius: '6px',
                        border: '1px solid rgba(77, 144, 120, 0.3)',
                        borderLeft: '4px solid #4d9078',
                    }}
                >
                    {verifyEmailMesage}
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <div className="login-form-group">
                    <label>Email or Username</label>
                    <input
                        type="text"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="login-form-group">
                    <label>Password</label>
                    <PasswordField
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        autoComplete="current-password"
                        aria-label="Password"
                    />
                </div>
                {error && <p className="login-error-message" style={{ color: '#a12622' }}>{error}</p>}
                <button type="submit"
                className={`login-submit-btn msv-auth-primary msv-shine ${loading ? 'disabled' : ''}`}
                disabled={loading}>
                    {loading ? 'Logging in...' : 'Log In'}
                </button>
            </form>
            <p style={{ textAlign: 'center', marginTop: '10px' }}>
                <button
                    type="button"
                    onClick={handleResetPassword}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        textDecoration: 'underline',
                        color: '#76d376',
                        cursor: 'pointer',
                    }}
                >
                    Reset Password
                </button>
            </p>
            <button
                className="login-create-account-btn msv-auth-secondary"
                onClick={handleCreateAccount}
            >
                Create New Account
            </button>
        </div>
    );

    // Main render function
    if (isAndroidWizard) {
        return (
            <div className="login-viewport">
                <div className="login-scroll" ref={scrollContainerRef}>
                    {renderForm()}
                </div>
            </div>
        );
    }

    // Non-Android: keep existing layout
    return (
        <div className="msv-auth-page">
            {renderForm()}
            <FooterSimple />
        </div>
    );
};

export default LoginPage;
